/**
 * `view` レイヤの定数。
 *
 * 演出の所要時間は一箇所に集約する（`04-architecture.md` effects）。実機で見て調整するため、
 * 値の根拠が「実装して決めた」ものであることを明記する（OI-010）。
 */

/**
 * ベベルの幅（論理 px）。分割数によらず固定（`04b-view-bevel.md` §1.1）。
 *
 * 隣り合う 2 ピースはそれぞれ自分の辺にベベルを持つため、継ぎ目は見た目で 4 px の谷になる。
 * 当初は 1 px だったが、実機で細すぎたため 2 px にした（TK-039）。
 */
export const BEVEL_W = 2;

/** 押しのけられたピースの移動（FR-020）。当初 300ms、実機の体感で 1.5 倍速の 200ms に短縮。 */
export const DISPLACE_MS = 200;

/**
 * 継ぎ目の消失・出現（FR-021）。
 *
 * 結合の成立を知らせる合図であり、押しのけの移動（`DISPLACE_MS`）より短くする。
 * 移動より長いと、ピースが着地したあとに継ぎ目だけが遅れて消えて見える。
 */
export const SEAM_FADE_MS = 150;

/**
 * ドロップ予測の枠のオフセット量（論理 px）。凹角の円弧半径もこれに等しい（FR-011）。
 *
 * 線幅 2 px（`board.css` `.preview`）の中心線をこの量だけ内側へ寄せると、線はピース境界から
 * 1〜3 px の帯に載る。ベベル帯（`BEVEL_W` = 2 px）に重なる位置で、隣のピースへはみ出さない。
 */
export const PREVIEW_OFFSET = 2;

/**
 * 新規結合のフラッシュ（TK-051）。
 *
 * 結合が成立した瞬間、そのセット全体に白の加算 50% がいきなり最大で点灯し、0 へ減衰する。
 * 立ち上がりを置かないのはリズムのため（ユーザー指定）。`board.css` の piece-flash と
 * 一致させること。
 */
export const JOIN_FLASH_MS = 300;

/**
 * 秘文字レイヤーの退場前の振動（TK-049、段階遷移）。
 *
 * 第 1 段階クリアの瞬間、レイヤー全体が細かく震えてから方角へ飛び去る。退場だけでは
 * あっさりしすぎるというフィードバックによる 1 拍目。`board.css` の secret-shake と
 * 一致させること。
 */
export const SECRET_SHAKE_MS = 300;

/**
 * 秘文字レイヤーのフェードアウト（FR-013、段階遷移）。
 *
 * 「文字が消えて新たな課題が現れる」文脈を与える演出（ADR-005）であり、短すぎると
 * 進捗が巻き戻ったようにしか見えない。実機で見て調整する（OI-010 と同じ運用）。
 * `board.css` の `--secret-fade` と一致させること。
 */
export const SECRET_FADE_MS = 600;

/**
 * 盤面外周の枠線（と盤外に面した辺の継ぎ目）のフェードアウト（FR-014、クリア演出）。
 *
 * 内部の継ぎ目の消失（`SEAM_FADE_MS`）に**続いて**走る 2 拍目であり、同じ長さにして
 * 「消える」リズムを揃える。`board.css` の `--frame-fade` と一致させること。
 */
export const FRAME_FADE_MS = 150;

/**
 * `Clear !` の表示保持時間（FR-014）。
 *
 * 読み終える前に消えない長さ。長すぎると `Next Stage` への到達が遅れるだけなので、
 * 1 秒強に留める。実機で見て調整する（OI-010 と同じ運用）。
 */
export const CLEAR_HOLD_MS = 1200;

/**
 * `Clear !` のフェードアウト（FR-014）。秘文字の消失（`SECRET_FADE_MS`）と同じ長さにして、
 * 「課題の区切りが消える」演出の速度を揃える。`main.css` の `--clear-fade` と一致させること。
 */
export const CLEAR_FADE_MS = 400;

/**
 * `Next Stage` のフェードイン（FR-015）。
 *
 * 出現は控えめでよい（リンクは押されるまで待つだけ）。場所は骨格が最初から確保しており、
 * 可視化してもレイアウトシフトは起きない。`main.css` の `--next-fade` と一致させること。
 */
export const NEXT_FADE_MS = 300;

/**
 * ドラッグ中の持ち上がりの高さ（論理 px。`04b-view-bevel.md` §1.3）。
 *
 * 高さは 2 値でありアニメーションしない（B-7）。`tools/bake/bake.py` の `DRAG_LIFT_PX` と
 * `board.css` の `--lift-px` に同じ値を持たせること（アトラスの z 行と影の式がこの値で
 * 焼かれている。docs/Reference/tk-029-bevel-bake.md）。
 */
export const DRAG_LIFT_PX = 24;

/**
 * 影のパネル光源（TK-030 / TK-042、`04b` §4）。中心座標（論理 px）は `tools/bake/bake.py` の
 * PANEL_CENTERS と一致させる。パネル高さ `SHADOW_PANEL_Z` は board.css の旧 --panel-z と同値。
 * 影オフセットの式 offset = (P − C_i) · h / (C_z − h) は drag-layer が計算する。
 */
export const SHADOW_PANELS: ReadonlyArray<{ readonly x: number; readonly y: number }> = [
  { x: 60, y: -40 },
  { x: -180, y: 140 },
  { x: 300, y: -260 },
];

/** パネル光源の高さ（論理 px）。 */
export const SHADOW_PANEL_Z = 1600;

/** 影のぼかし（feGaussianBlur の stdDeviation。box-shadow の blur ≈ 2σ に合わせる）。 */
export const SHADOW_BLUR_STD = (140 * DRAG_LIFT_PX) / (SHADOW_PANEL_Z - DRAG_LIFT_PX);
